import DT from "duration-time-conversion"
import React, {useState, useEffect, useCallback, useRef, createRef, memo} from "react"
import clamp from "lodash/clamp"
import debounce from "lodash/debounce"
import throttle from "lodash/throttle"
import {getKeyCode} from "../../utils"
import Timeline from "../timelines/Timeline"
import Metronome from "../metronome/Metronome"
import playActionIcon from "../../../assets/icon/play-action.svg"
import pauseActionIcon from "../../../assets/icon/pause-action.svg"
import forwardIcon from "../../../assets/icon/forward.svg"
import rewindIcon from "../../../assets/icon/rewind.svg"
import {useLocalContext} from "../../../reactContext/contextCustomHooks/useLocalContext"
import PeaksWaveform from "../peaks-waveform/PeaksWaveform"

const Progress = ({ player, currentTime}) => {
    const [grabbing, setGrabbing] = useState(false)
    const barRef = useRef(null)

    const getTimeFromEvent = useCallback((event) => {
        if (!barRef.current || !player?.duration) return 0
        const rect = barRef.current.getBoundingClientRect()
        const x = event.clientX - rect.left
        const ratio = clamp(x / rect.width, 0, 1)
        return ratio * player.duration
    }, [player])

    const onProgressClick = useCallback((event) => {
        if (event.button !== 0) return
        player.currentTime = getTimeFromEvent(event)
    }, [getTimeFromEvent, player])

    const onGrabDown = useCallback((event) => {
        if (event.button !== 0) return
        event.preventDefault()
        setGrabbing(true)
    }, [])

    const onGrabMove = useCallback((event) => {
        if (!grabbing) return
        player.currentTime = getTimeFromEvent(event)
    }, [grabbing, getTimeFromEvent, player])

    const onGrabUp = useCallback(() => {
        setGrabbing(false)
    }, [])

    useEffect(() => {
        if (!grabbing) return
        document.addEventListener("mousemove", onGrabMove)
        document.addEventListener("mouseup", onGrabUp)
        return () => {
            document.removeEventListener("mousemove", onGrabMove)
            document.removeEventListener("mouseup", onGrabUp)
        }
    }, [grabbing, onGrabMove, onGrabUp])

    return (
        <div ref={barRef} className="progress" onMouseDown={onProgressClick}>
            <div className="bar" style={{width: `${(currentTime / player.duration) * 100}%`}}>
                <div className="handle" onMouseDown={onGrabDown} />
            </div>
        </div>
    )
}

const Actions = ({ player, duration, playing, currentTime, setPlaying, setGoRewind, setGoForward }) => {
	const [isStart, setIsStart] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

    const getDuration = useCallback((time) => {
        time = time === Infinity ? 0 : time
        return DT.d2t(time).split(".")[0]
    }, [])

    const onClickPlay = useCallback(() => {
        setPlaying(true)
        player.play()
    }, [player])

    const onClickPause = useCallback(() => {
        setPlaying(false)
        player.pause()
    }, [player])

	const onClickRewind = useCallback(() => {
		if(player.currentTime === 0) return;
		setGoRewind(true);
		setTimeout(() => setGoRewind(false), 1000);
        player.currentTime = Math.max(player.currentTime - 1, 0)
    }, [player])

	const onClickForward = useCallback(() => {
		if(player.currentTime === player.duration) return;
		setGoForward(true);
		setTimeout(() => setGoForward(false), 1000);
        player.currentTime = Math.min(player.currentTime + 1, player.duration)
    }, [player])

	const onKeyDown = useCallback(
		(event) => {
			const keyCode = getKeyCode(event)
			switch (keyCode) {
				case 37:
					onClickRewind();
					break;
				case 39:
					onClickForward();
					break;
				default:
					break
			}
		},
		[player],
	)

	useEffect(() => {
		setIsStart(player.currentTime === 0)
		setIsEnd(player.currentTime === duration)
    }, [player.currentTime, duration])

	useEffect(() => {
		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("keydown", onKeyDown)
		}
	}, [onKeyDown])

	return (
        <div className="subplayer-actions">
            <span className="current-timer">{getDuration(currentTime)}</span>
                <button className={['rwd', isStart ? 'hidden' : ''].join(" ").trim()} onClick={onClickRewind}><img src={rewindIcon} alt=""/></button>
                {!playing || isEnd ?
                    <button className={['play'].join(" ").trim()} onClick={onClickPlay}><img src={playActionIcon} alt=""/></button>
                    :
                    <button className={['pause'].join(" ").trim()} onClick={onClickPause}><img src={pauseActionIcon} alt=""/></button>
                }
                <button className={['fwd', isEnd ? 'hidden' : ''].join(" ").trim()} onClick={onClickForward}><img src={forwardIcon} alt=""/></button>
            <span className="total-timer">{getDuration(duration || 0)}</span>
        </div>
    )
}

export default function Footer(props) {
    const {localContext} = useLocalContext()
    const $footer = useRef(null)
    const [render, setRender] = useState({
        padding: 2,
        duration: 10,
        beginTime: -5,
    })

	const [bounds, setBounds] = useState({
		width: document.body.clientWidth,
		x : 0
	});

	const resize = useCallback(() => {
		const { width, left:x } = $footer.current.getBoundingClientRect();
		setBounds({ width, x});
	}, [$footer, setBounds])

	useEffect(() => {
		resize()
		const debouncedResize = debounce(resize, 300)
		window.addEventListener("resize", debouncedResize)
		return () => window.removeEventListener("resize", debouncedResize)
	}, [localContext.minNavigation])

    const onWheel = useCallback((event) => {
        if (
            !props.player ||
            !props.waveform ||
            props.player.playing ||
            !$footer.current ||
            !$footer.current.contains(event.target)
        ) {
            return
        }

        const deltaY = Math.sign(event.deltaY) / 5
        const currentTime = clamp(props.player.currentTime + deltaY, 0, props.player.duration)
        props.player.currentTime = currentTime
        props.waveform.seek(currentTime)

    }, [props.waveform, props.player, $footer])

    useEffect(() => {
        const onWheelThrottle = throttle(onWheel, 100)
        window.addEventListener("wheel", onWheelThrottle)
        return () => window.removeEventListener("wheel", onWheelThrottle)
    }, [onWheel])

	useEffect(() => {
		if (props.waveform) {
			props.waveform.setOptions({ mediaElement: props.player })
			props.waveform.controller.destroy()
			props.waveform.controller.playInit()
		}
	}, [props.waveform, props.player])

    return (
        <div className="subplayer-footer" ref={$footer}>
            {props.player ? (
                <React.Fragment>
                    <Progress {...props}/>
                    <Actions {...props}/>
					<Timeline {...props} render={render} bounds={bounds}/>
                    {(props.isLoadingWaveform || props.isErrorWaveform || props.waveformUrl?.message) ? null : <PeaksWaveform player={props.player} waveformUrl={props.waveformUrl} setPlaying={props.setPlaying} setRender={setRender}/>}
                    <Metronome {...props} render={render} bounds={bounds}/>
                </React.Fragment>
            ) : null}
        </div>
    )
}
